import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ENV } from '../environment';
import { CapabilitiesDto } from '../models/capabilities.model';

/**
 * Service for managing feature capabilities and subscription limits.
 * This service provides reactive access to the user's current plan features
 * and limits, enabling feature gating throughout the application.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureCapabilitiesService {
  private http = inject(HttpClient);
  private apiUrl = `${ENV.baseUrl}features`;

  // BehaviorSubject for reactive capabilities updates
  private capabilitiesSubject = new BehaviorSubject<CapabilitiesDto | null>(null);

  /**
   * Observable stream of current capabilities.
   * Components can subscribe to this to react to capability changes.
   */
  public capabilities$ = this.capabilitiesSubject.asObservable();

  /**
   * Fetches current user's capabilities from the backend.
   * Updates the internal state and returns the capabilities.
   */
  getCapabilities(): Observable<CapabilitiesDto> {
    return this.http.get<CapabilitiesDto>(`${this.apiUrl}/capabilities`, {
      withCredentials: true
    }).pipe(
      tap(capabilities => {
        this.capabilitiesSubject.next(capabilities);
      }),
      catchError(error => {
        console.error('Failed to fetch capabilities:', error);
        // Return a default free plan on error
        const defaultCapabilities: CapabilitiesDto = {
          planCode: 'free',
          planName: 'Free',
          features: {},
          limits: {},
          isAdmin: false,
          trialing: false
        };
        this.capabilitiesSubject.next(defaultCapabilities);
        return of(defaultCapabilities);
      })
    );
  }

  /**
   * Refreshes capabilities from the server.
   * Useful after subscription changes or manual refresh.
   */
  refreshCapabilities(): Observable<CapabilitiesDto> {
    return this.getCapabilities();
  }

  /**
   * Checks if the current plan has access to a specific feature.
   * @param featureKey The feature key to check (e.g., 'funnel_analysis')
   * @returns true if the feature is available, false otherwise
   */
  hasFeature(featureKey: string): boolean {
    const capabilities = this.capabilitiesSubject.value;
    if (!capabilities) {
      return false;
    }

    // Admin users have access to all features
    if (capabilities.isAdmin) {
      return true;
    }

    return capabilities.features[featureKey] === true;
  }

  /**
   * Gets the limit value for a specific limit key.
   * @param limitKey The limit key to retrieve (e.g., 'max_sites')
   * @returns The limit value, or null if not found
   */
  getLimit(limitKey: string): any {
    const capabilities = this.capabilitiesSubject.value;
    if (!capabilities) {
      return null;
    }

    return capabilities.limits[limitKey];
  }

  /**
   * Gets the current capabilities synchronously.
   * @returns Current capabilities or null if not loaded
   */
  getCurrentCapabilities(): CapabilitiesDto | null {
    return this.capabilitiesSubject.value;
  }

  /**
   * Checks if the user is currently on a trial.
   * @returns true if trialing, false otherwise
   */
  isTrialing(): boolean {
    const capabilities = this.capabilitiesSubject.value;
    return capabilities?.trialing ?? false;
  }

  /**
   * Gets the trial end date if applicable.
   * @returns Trial end date string or null
   */
  getTrialEndDate(): string | null {
    const capabilities = this.capabilitiesSubject.value;
    return capabilities?.trialEndsAt ?? null;
  }

  /**
   * Gets the current plan code.
   * @returns Plan code (e.g., 'free', 'pro', 'enterprise')
   */
  getPlanCode(): string {
    const capabilities = this.capabilitiesSubject.value;
    return capabilities?.planCode ?? 'free';
  }

  /**
   * Gets the current plan name.
   * @returns Plan name (e.g., 'Free', 'Pro', 'Enterprise')
   */
  getPlanName(): string {
    const capabilities = this.capabilitiesSubject.value;
    return capabilities?.planName ?? 'Free';
  }

  /**
   * Checks if the user is an admin.
   * @returns true if admin, false otherwise
   */
  isAdmin(): boolean {
    const capabilities = this.capabilitiesSubject.value;
    return capabilities?.isAdmin ?? false;
  }

  /**
   * Clears the capabilities state.
   * Useful on logout.
   */
  clearCapabilities(): void {
    this.capabilitiesSubject.next(null);
  }

  /**
   * Returns the minimum plan required for a feature.
   * Used to display plan badges in the UI during trial period.
   * Based on feature-matrix.yml configuration.
   * @param featureKey The feature key to check
   * @returns The minimum plan name (GROWTH, BUSINESS, ENTERPRISE) or null if feature is available on all plans
   */
  getRequiredPlan(featureKey: string): string | null {
    // Map of features to their minimum required plans
    // Based on feature-matrix.yml
    const featurePlanMap: Record<string, string> = {
      // GROWTH features
      'enhanced_event_tracking': 'GROWTH',
      'ecommerce_events': 'GROWTH',
      'advanced_dashboard': 'GROWTH',
      'cohort_analysis': 'GROWTH',
      'funnel_analysis': 'GROWTH',
      'user_journey': 'GROWTH',
      'query_builder': 'GROWTH',
      'cross_site_comparison': 'GROWTH',
      'basic_role_management': 'GROWTH',
      'google_analytics_import': 'GROWTH',
      'google_search_console': 'GROWTH',
      'data_warehouse_sync': 'GROWTH',
      'scheduled_reports': 'GROWTH',
      'advanced_exports': 'GROWTH',
      'custom_dashboard_cards': 'GROWTH',

      // BUSINESS features
      'behavior_analytics': 'BUSINESS',
      'heatmaps': 'BUSINESS',
      'ab_testing': 'BUSINESS',
      'specialized_analytics': 'BUSINESS',
      'site_grouping': 'BUSINESS',
      'multi_site_dashboards': 'BUSINESS',
      'advanced_rbac': 'BUSINESS',
      'sso': 'BUSINESS',
      'two_factor_auth': 'BUSINESS',
      'ip_whitelisting': 'BUSINESS',
      'audit_logs': 'BUSINESS',
      'advanced_data_controls': 'BUSINESS',
      'data_pipeline_integrations': 'BUSINESS',
      'marketing_tool_integrations': 'BUSINESS',
      'api_access': 'BUSINESS',
      'rest_api': 'BUSINESS',
      'graphql_api': 'BUSINESS',
      'webhook_support': 'BUSINESS',
      'api_key_management': 'BUSINESS',
      'sdk_libraries': 'BUSINESS',
      'managed_proxy': 'BUSINESS',
      'enhanced_security': 'BUSINESS',
      'priority_support': 'BUSINESS',

      // ENTERPRISE features
      'ai_powered_insights': 'ENTERPRISE',
      'custom_machine_learning': 'ENTERPRISE',
      'advanced_compliance': 'ENTERPRISE',
      'data_governance': 'ENTERPRISE',
      'dedicated_data_residency': 'ENTERPRISE',
      'advanced_audit_trails': 'ENTERPRISE',
      'dedicated_api_endpoints': 'ENTERPRISE',
      'high_traffic_support': 'ENTERPRISE',
      'self_hosting': 'ENTERPRISE',
      'enterprise_security': 'ENTERPRISE',
      'custom_development': 'ENTERPRISE',
      'white_label': 'ENTERPRISE',
      'dedicated_support': 'ENTERPRISE',
      'professional_services': 'ENTERPRISE'
    };

    return featurePlanMap[featureKey] || null;
  }
}
